import React, { useState, useEffect, useRef } from 'react';
import { X, Home, MapPin, DollarSign, Maximize2, Calendar, Eye, MessageSquare, CheckCircle, XCircle, Users, ChevronDown, ChevronUp, Building2, User } from 'lucide-react';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import toast from 'react-hot-toast';
import type { Client, FormData, PropertyClientMatch } from '../types';
import { cities, neighborhoods } from '../data';
import { normalizeCityName } from '../utils/cityNormalization';
import {
  calculateMatchScore,
  scoreBuyerAgainstSeller,
  type MatchScoreResult,
  type PropertyData,
  type BuyerCriteria,
  DEFAULT_MIN_SCORE,
  WEAK_TOGGLE_MIN_SCORE,
} from '../utils/matchingScoreEngine';
import { evaluateCandidate, type CandidateResult } from '../utils/candidateGate';
import { calculateConfidence } from '../utils/matchConfidence';
import { useMatchRecommendation, AI_MATCH_RECOMMENDATION_LIMIT, AI_MATCH_MIN_SCORE } from '../utils/useMatchRecommendation';
import { rankMatches, type RankedMatch } from '../utils/matchRanking';
import MatchScoreDisplay from './MatchScoreDisplay';
import AIMatchRecommendation from './AIMatchRecommendation';

interface ClientPropertiesModalProps {
  client: Client;
  onClose: () => void;
}

const propertyTypes = [
  { value: 'apartment', label: 'דירת מגורים' },
  { value: 'duplex', label: 'דופלקס' },
  { value: 'penthouse', label: 'פנטהאוז' },
  { value: 'garden-apartment', label: 'דירת גן' },
  { value: 'house', label: 'בית פרטי' },
  { value: 'commercial', label: 'מסחרי' },
];

const statusOptions = [
  { value: 'new', label: 'חדש', color: 'bg-blue-500', icon: Eye },
  { value: 'interested', label: 'מעוניין', color: 'bg-yellow-500', icon: Eye },
  { value: 'viewed', label: 'סייר בנכס', color: 'bg-purple-500', icon: Eye },
  { value: 'offer_made', label: 'הציע הצעה', color: 'bg-orange-500', icon: CheckCircle },
  { value: 'rejected', label: 'דחה', color: 'bg-red-500', icon: XCircle },
  { value: 'closed', label: 'עסקה נסגרה', color: 'bg-green-700', icon: CheckCircle },
];

interface Activity {
  id: string;
  activity_type: string;
  description: string;
  created_at: string;
}

const activityTypeLabels: Record<string, string> = {
  call: 'שיחה',
  whatsapp: 'WhatsApp',
  meeting: 'פגישה',
  property_sent: 'שליחת נכס',
  note: 'הערה',
};

type MatchScope = 'agent' | 'office';

interface OfficePropertyResponse {
  success: boolean;
  properties?: FormData[];
  error?: string;
}

const ClientPropertiesModal: React.FC<ClientPropertiesModalProps> = ({ client, onClose }) => {
  const [properties, setProperties] = useState<FormData[]>([]);
  const [matchingBuyers, setMatchingBuyers] = useState<Client[]>([]);
  const [matches, setMatches] = useState<PropertyClientMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<FormData | null>(null);
  const [showNotes, setShowNotes] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesOpen, setActivitiesOpen] = useState(false);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [showWeakMatches, setShowWeakMatches] = useState(false);
  const [showFiltered, setShowFiltered] = useState(false);
  const [rankedProperties, setRankedProperties] = useState<RankedMatch<FormData>[]>([]);
  const [rankedBuyers, setRankedBuyers] = useState<RankedMatch<Client>[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Array<{ property: FormData; gate: CandidateResult }>>([]);
  const [filteredBuyers, setFilteredBuyers] = useState<Array<{ buyer: Client; gate: CandidateResult }>>([]);
  const [matchScope, setMatchScope] = useState<MatchScope>('agent');
  const [hasOffice, setHasOffice] = useState<boolean | null>(null);
  const { getState: getRecState, generate: generateRec } = useMatchRecommendation();

  // Session-level cache for office properties (survives scope toggles within modal lifecycle)
  const officePropertiesCache = useRef<FormData[] | null>(null);
  // Session-level cache for agent properties (avoid re-fetching Firestore on toggle back)
  const agentPropertiesCache = useRef<FormData[] | null>(null);

  // Check office membership on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!auth.currentUser) return;
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-office`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (cancelled) return;
        const officeExists = !!(data.success && data.office && data.office.members && data.office.members.length > 1);
        setHasOffice(officeExists);
        if (officeExists && client.leadType !== 'seller') {
          setMatchScope('office'); // default to office mode for buyers in offices
        }
      } catch {
        if (!cancelled) setHasOffice(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (client.leadType === 'seller') {
      loadMatchingBuyers();
    } else {
      loadMatchingProperties(matchScope);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, matchScope]);

  const loadActivities = async () => {
    if (!auth.currentUser) return;
    setActivitiesLoading(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/get-activities?leadId=${client.id}`, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setActivities(data.activities || []);
      }
    } catch (err) {
      console.error('Failed to load activities', err);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const toggleActivities = () => {
    const next = !activitiesOpen;
    setActivitiesOpen(next);
    if (next && activities.length === 0) {
      loadActivities();
    }
  };

  const normalizeNeighborhoodName = (neighborhoodName: string | undefined, cityValue: string | undefined): string | undefined => {
    if (!neighborhoodName || !cityValue) return undefined;
    const cityNeighborhoods = neighborhoods[cityValue] || [];
    const neighborhoodObj = cityNeighborhoods.find(n => n.label === neighborhoodName || n.value === neighborhoodName);
    return neighborhoodObj ? neighborhoodObj.value : neighborhoodName;
  };

  const loadMatchingBuyers = async () => {
    if (!auth.currentUser) return;

    try {
      setLoading(true);

      const idToken = await auth.currentUser.getIdToken();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const apiUrl = `${supabaseUrl}/functions/v1/get-leads`;

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch leads');
      }

      const data = await response.json();
      const allBuyers = data.leads.filter((lead: Client) => lead.leadType === 'buyer');

      const sellerCriteria = client.searchCriteria;
      const sellerProperty: PropertyData = {
        price: sellerCriteria.priceMin || sellerCriteria.priceMax,
        rooms: sellerCriteria.rooms && sellerCriteria.rooms.length === 1 ? sellerCriteria.rooms[0] : undefined,
        size: sellerCriteria.minSize || sellerCriteria.maxSize,
        propertyType: sellerCriteria.propertyTypes && sellerCriteria.propertyTypes.length === 1 ? sellerCriteria.propertyTypes[0] : undefined,
        city: sellerCriteria.cities && sellerCriteria.cities.length === 1 ? normalizeCityName(sellerCriteria.cities[0]) : undefined,
        neighborhood: sellerCriteria.neighborhoods && sellerCriteria.neighborhoods.length === 1 ? sellerCriteria.neighborhoods[0] : undefined,
      };

      const candidates: Array<{ buyer: Client; score: MatchScoreResult }> = [];
      const filtered: Array<{ buyer: Client; gate: CandidateResult }> = [];

      for (const buyer of allBuyers) {
        const buyerCriteria = buyer.searchCriteria;
        if (!buyerCriteria) continue;

        const criteria: BuyerCriteria = {
          priceMin: buyerCriteria.priceMin,
          priceMax: buyerCriteria.priceMax,
          rooms: buyerCriteria.rooms,
          cities: buyerCriteria.cities,
          neighborhoods: buyerCriteria.neighborhoods,
          propertyTypes: buyerCriteria.propertyTypes,
          minSize: buyerCriteria.minSize,
          maxSize: buyerCriteria.maxSize,
        };

        const gate = evaluateCandidate(sellerProperty, criteria);
        if (!gate.isCandidate) {
          filtered.push({ buyer, gate });
          continue;
        }

        const score = scoreBuyerAgainstSeller(criteria, sellerProperty);
        if (score.hardMatchPassed) {
          candidates.push({ buyer, score });
        }
      }

      const ranked = rankMatches(candidates.map(c => ({ item: c.buyer, score: c.score })));

      setRankedBuyers(ranked);
      setMatchingBuyers(ranked.map(r => r.item));
      setFilteredBuyers(filtered);

    } catch (error) {
      console.error('Error loading buyers:', error);
      toast.error('שגיאה בטעינת קונים מתאימים');
    } finally {
      setLoading(false);
    }
  };

  const fetchOfficeProperties = async (): Promise<FormData[]> => {
    if (officePropertiesCache.current) return officePropertiesCache.current;
    if (!auth.currentUser) return [];
    const token = await auth.currentUser.getIdToken();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-office-properties`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to fetch office properties');
    const data: OfficePropertyResponse = await res.json();
    if (!data.success || !data.properties) throw new Error(data.error || 'Failed to fetch office properties');
    const props = data.properties as FormData[];
    officePropertiesCache.current = props;
    return props;
  };

  const fetchAgentProperties = async (): Promise<FormData[]> => {
    if (agentPropertiesCache.current) return agentPropertiesCache.current;
    const formsRef = collection(db, 'forms');
    const q = query(formsRef, where('userId', '==', auth.currentUser!.uid));
    const snapshot = await getDocs(q);
    const props = snapshot.docs.map(doc => ({
      formId: doc.id,
      ...doc.data()
    })) as FormData[];
    agentPropertiesCache.current = props;
    return props;
  };

  const loadMatchingProperties = async (scope: MatchScope = matchScope) => {
    if (!auth.currentUser) return;

    try {
      if (officePropertiesCache.current || agentPropertiesCache.current) {
        setScopeLoading(true); // lightweight loading for scope switch
      } else {
        setLoading(true); // full loading for first load
      }

      let allProperties: FormData[];
      if (scope === 'office' && hasOffice) {
        try {
          allProperties = await fetchOfficeProperties();
        } catch (err) {
          console.error('Office properties fetch failed, falling back to agent', err);
          allProperties = await fetchAgentProperties();
        }
      } else {
        allProperties = await fetchAgentProperties();
      }

      const criteria = client.searchCriteria;

      const buyerCriteria: BuyerCriteria = {
        priceMin: criteria.priceMin,
        priceMax: criteria.priceMax,
        rooms: criteria.rooms,
        cities: criteria.cities,
        neighborhoods: criteria.neighborhoods,
        propertyTypes: criteria.propertyTypes,
        minSize: criteria.minSize,
        maxSize: criteria.maxSize,
      };

      const candidates: Array<{ property: FormData; score: MatchScoreResult }> = [];
      const filtered: Array<{ property: FormData; gate: CandidateResult }> = [];

      for (const prop of allProperties) {
        if (!prop.property || !prop.address) continue;

        const propertyCity = normalizeCityName(prop.address?.city);
        const propertyNeighborhood = normalizeNeighborhoodName(prop.address?.neighborhood, propertyCity);

        const propData: PropertyData = {
          price: prop.property.price,
          rooms: prop.property.rooms,
          size: prop.property.size,
          propertyType: prop.property.propertyType,
          city: propertyCity,
          neighborhood: propertyNeighborhood,
          hasParking: (prop.property.parkingSpots ?? 0) > 0,
          hasElevator: prop.property.hasElevator,
          hasSaferoom: prop.property.hasSaferoom,
          hasBalcony: prop.property.hasBalcony,
          propertyCondition: prop.property.propertyCondition,
        };

        const gate = evaluateCandidate(propData, buyerCriteria);
        if (!gate.isCandidate) {
          filtered.push({ property: prop, gate });
          continue;
        }

        const score = calculateMatchScore(propData, buyerCriteria);
        if (score.hardMatchPassed) {
          candidates.push({ property: prop, score });
        }
      }

      const ranked = rankMatches(candidates.map(c => ({ item: c.property, score: c.score })));

      setRankedProperties(ranked);
      setProperties(ranked.map(r => r.item));
      setFilteredProperties(filtered);

      const matchesRef = collection(db, 'property_client_matches');
      const matchesQuery = query(
        matchesRef,
        where('clientId', '==', client.id),
        where('userId', '==', auth.currentUser.uid)
      );
      const matchesSnapshot = await getDocs(matchesQuery);
      const matchesData = matchesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as PropertyClientMatch[];

      setMatches(matchesData);

    } catch (error) {
      console.error('Error loading properties:', error);
    } finally {
      setLoading(false);
      setScopeLoading(false);
    }
  };

  const handleUpdateStatus = async (formId: string, status: PropertyClientMatch['status']) => {
    if (!auth.currentUser) {
      toast.error('יש להתחבר כדי לעדכן סטטוס');
      return;
    }

    try {
      const existingMatch = matches.find(m => m.formId === formId);

      if (existingMatch) {
        await updateDoc(doc(db, 'property_client_matches', existingMatch.id), {
          status,
          updatedAt: new Date().toISOString()
        });

        setMatches(prev => prev.map(m =>
          m.id === existingMatch.id ? { ...m, status } : m
        ));
      } else {
        const newMatch = {
          clientId: client.id,
          formId,
          status,
          userId: auth.currentUser.uid,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        const docRef = await addDoc(collection(db, 'property_client_matches'), newMatch);
        setMatches(prev => [...prev, { id: docRef.id, ...newMatch }]);
      }

      toast.success('הסטטוס עודכן בהצלחה');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('שגיאה בעדכון הסטטוס');
    }
  };

  const handleSaveNote = async (formId: string) => {
    if (!noteText.trim()) return;
    if (!auth.currentUser) {
      toast.error('יש להתחבר כדי לשמור הערה');
      return;
    }

    try {
      const existingMatch = matches.find(m => m.formId === formId);

      if (existingMatch) {
        await updateDoc(doc(db, 'property_client_matches', existingMatch.id), {
          notes: noteText.trim(),
          updatedAt: new Date().toISOString()
        });

        setMatches(prev => prev.map(m =>
          m.id === existingMatch.id ? { ...m, notes: noteText.trim() } : m
        ));
      } else {
        const newMatch = {
          clientId: client.id,
          formId,
          status: 'new' as const,
          notes: noteText.trim(),
          userId: auth.currentUser.uid,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        const docRef = await addDoc(collection(db, 'property_client_matches'), newMatch);
        setMatches(prev => [...prev, { id: docRef.id, ...newMatch }]);
      }

      setNoteText('');
      setShowNotes(null);
      toast.success('ההערה נשמרה בהצלחה');
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('שגיאה בשמירת ההערה');
    }
  };

  const getMatchForProperty = (formId: string) => {
    return matches.find(m => m.formId === formId);
  };

  const getStatusBadge = (formId: string) => {
    const match = getMatchForProperty(formId);
    if (!match) return null;

    const status = statusOptions.find(s => s.value === match.status);
    if (!status) return null;

    return (
      <span className={`${status.color} text-white text-xs px-3 py-1 rounded-full font-medium`}>
        {status.label}
      </span>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-gray-800 rounded-lg w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-700">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">
              {client.leadType === 'seller' ? 'קונים מתאימים ל-' : 'נכסים מתאימים ל-'}{client.name}
            </h2>
            <p className="text-blue-100">
              {client.leadType === 'seller'
                ? `${rankedBuyers.filter(r => r.score.score >= DEFAULT_MIN_SCORE).length} התאמות איכות${rankedBuyers.filter(r => r.score.score < DEFAULT_MIN_SCORE && r.score.score >= WEAK_TOGGLE_MIN_SCORE).length > 0 ? ` (+${rankedBuyers.filter(r => r.score.score < DEFAULT_MIN_SCORE && r.score.score >= WEAK_TOGGLE_MIN_SCORE).length} חלשים)` : ''}${filteredBuyers.length > 0 ? ` · ${filteredBuyers.length} סוננו` : ''}`
                : `${rankedProperties.filter(r => r.score.score >= DEFAULT_MIN_SCORE).length} התאמות איכות${rankedProperties.filter(r => r.score.score < DEFAULT_MIN_SCORE && r.score.score >= WEAK_TOGGLE_MIN_SCORE).length > 0 ? ` (+${rankedProperties.filter(r => r.score.score < DEFAULT_MIN_SCORE && r.score.score >= WEAK_TOGGLE_MIN_SCORE).length} חלשים)` : ''}${filteredProperties.length > 0 ? ` · ${filteredProperties.length} סוננו` : ''}`
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleActivities}
                className="flex items-center gap-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
              >
                {activitiesOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                היסטוריית פעילות
              </button>
            </div>
            {client.leadType !== 'seller' && hasOffice && (
              <div className="flex items-center gap-2 bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setMatchScope('agent')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    matchScope === 'agent'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  הנכסים שלי
                </button>
                <button
                  onClick={() => setMatchScope('office')}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    matchScope === 'office'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  כל נכסי המשרד
                </button>
              </div>
            )}
            {client.leadType !== 'seller' && matchScope === 'office' && hasOffice && (
              <div className="w-full text-sm text-gray-300">
                {(() => {
                  const qualityMatches = rankedProperties.filter(r => r.score.score >= DEFAULT_MIN_SCORE);
                  const ownCount = qualityMatches.filter(r => r.item.isOwnProperty).length;
                  const otherCount = qualityMatches.length - ownCount;
                  return (
                    <span>
                      <span className="font-bold text-white">{qualityMatches.length}</span> התאמות איכותיות
                      {otherCount > 0 && (
                        <span className="text-gray-400"> · <span className="text-amber-300">{otherCount}</span> מסוכנים אחרים</span>
                      )}
                      {ownCount > 0 && otherCount > 0 && (
                        <span className="text-gray-400"> · <span className="text-blue-300">{ownCount}</span> שלך</span>
                      )}
                    </span>
                  );
                })()}
              </div>
            )}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showWeakMatches}
                  onChange={(e) => setShowWeakMatches(e.target.checked)}
                  className="rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                />
                הצג גם התאמות חלשות
              </label>
              {((client.leadType === 'seller' ? filteredBuyers.length : filteredProperties.length) > 0) && (
                <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showFiltered}
                    onChange={(e) => setShowFiltered(e.target.checked)}
                    className="rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                  />
                  הצג נכסים שסוננו
                </label>
              )}
            </div>
          </div>

          {activitiesOpen && (
            <div className="mb-4 border border-gray-700 rounded-lg bg-gray-750 max-h-48 overflow-y-auto">
              {activitiesLoading ? (
                <div className="p-4 text-center text-gray-400 text-sm">טוען...</div>
              ) : activities.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">אין פעילויות</div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {activities.map(a => {
                    const d = new Date(a.created_at);
                    const pad = (n: number) => n.toString().padStart(2, '0');
                    const dateStr = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                    return (
                      <div key={a.id} className="px-4 py-2.5 flex items-start gap-3">
                        <span className="shrink-0 px-2 py-0.5 rounded bg-gray-600 text-gray-200 text-xs font-medium mt-0.5">
                          {activityTypeLabels[a.activity_type] || a.activity_type}
                        </span>
                        <div className="flex-1 min-w-0">
                          {a.description && <p className="text-sm text-gray-300 truncate">{a.description}</p>}
                          <p className="text-xs text-gray-500 mt-0.5">{dateStr}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {loading || scopeLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-gray-400">
                  {scopeLoading
                    ? (matchScope === 'office' ? 'טוען נכסי משרד...' : 'טוען נכסים...')
                    : (client.leadType === 'seller' ? 'טוען קונים...' : 'טוען נכסים...')
                  }
                </p>
              </div>
            </div>
          ) : client.leadType === 'seller' ? (
            rankedBuyers.filter(r => showWeakMatches ? r.score.score >= WEAK_TOGGLE_MIN_SCORE : r.score.score >= DEFAULT_MIN_SCORE).length === 0 ? (
              <div className="text-center py-12">
                <Users size={64} className="mx-auto text-gray-600 mb-4" />
                <p className="text-xl text-gray-400 mb-2">לא נמצאו קונים מתאימים</p>
                <p className="text-gray-500">נסה לשנות את קריטריוני החיפוש של המוכר</p>
              </div>
            ) : (
              <div className="space-y-4">
                {rankedBuyers
                  .filter(r => showWeakMatches ? r.score.score >= WEAK_TOGGLE_MIN_SCORE : r.score.score >= DEFAULT_MIN_SCORE)
                  .map(({ item: buyer, score: matchScore, rankPosition, totalQualityMatches }) => (
                  <div
                    key={buyer.id}
                    className="bg-gray-750 border border-gray-700 rounded-lg p-6 hover:border-blue-500 transition-all"
                  >
                    <MatchScoreDisplay result={matchScore} rankPosition={rankPosition} totalQualityMatches={totalQualityMatches} />
                    {(() => {
                      const sc = client.searchCriteria;
                      const sellerProp: PropertyData = {
                        price: sc.priceMin || sc.priceMax,
                        rooms: sc.rooms && sc.rooms.length === 1 ? sc.rooms[0] : undefined,
                        size: sc.minSize || sc.maxSize,
                        propertyType: sc.propertyTypes && sc.propertyTypes.length === 1 ? sc.propertyTypes[0] : undefined,
                        city: sc.cities && sc.cities.length === 1 ? normalizeCityName(sc.cities[0]) : undefined,
                        neighborhood: sc.neighborhoods && sc.neighborhoods.length === 1 ? sc.neighborhoods[0] : undefined,
                      };
                      const buyerCrit: BuyerCriteria = {
                        priceMin: buyer.searchCriteria.priceMin,
                        priceMax: buyer.searchCriteria.priceMax,
                        rooms: buyer.searchCriteria.rooms,
                        cities: buyer.searchCriteria.cities,
                        neighborhoods: buyer.searchCriteria.neighborhoods,
                        propertyTypes: buyer.searchCriteria.propertyTypes,
                        minSize: buyer.searchCriteria.minSize,
                        maxSize: buyer.searchCriteria.maxSize,
                      };
                      const conf = calculateConfidence(sellerProp, buyerCrit);
                      const recState = getRecState(client.id, buyer.id);
                      const topN = rankedBuyers
                        .filter(r => r.score.score >= AI_MATCH_MIN_SCORE)
                        .slice(0, AI_MATCH_RECOMMENDATION_LIMIT)
                        .some(r => r.item.id === buyer.id);
                      if (!topN) return null;
                      return (
                        <AIMatchRecommendation
                          buyerId={client.id}
                          propertyId={buyer.id}
                          loading={recState.loading}
                          error={recState.error}
                          recommendation={recState.recommendation}
                          confidence={conf}
                          matchScore={matchScore.score}
                          rankPosition={rankPosition}
                          totalQualityMatches={totalQualityMatches}
                          onGenerate={() => generateRec(client.id, buyer.id, buyerCrit, sellerProp, matchScore, { rankPosition, totalQualityMatches, matchScope: 'agent' })}
                        />
                      );
                    })()}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-white mb-2">{buyer.name}</h3>
                        <div className="mb-3">
                          <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-blue-600 text-white">
                            🔍 קונה מעוניין
                          </span>
                        </div>
                        <div className="space-y-2">
                          <p className="text-gray-300 flex items-center gap-2">
                            <span>📱</span>
                            <a href={`tel:${buyer.phone}`} className="hover:text-blue-400 transition-colors">
                              {buyer.phone}
                            </a>
                          </p>
                          {buyer.email && (
                            <p className="text-gray-300 flex items-center gap-2">
                              <span>✉️</span>
                              <a href={`mailto:${buyer.email}`} className="hover:text-blue-400 transition-colors">
                                {buyer.email}
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-700 pt-4 mt-4">
                      <h4 className="text-sm font-semibold text-gray-400 mb-3">מה הקונה מחפש:</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {buyer.searchCriteria.priceMin && (
                          <div className="bg-gray-700 rounded px-3 py-2">
                            <span className="text-gray-400">תקציב מ: </span>
                            <span className="text-white font-medium">
                              ₪{buyer.searchCriteria.priceMin.toLocaleString()}
                            </span>
                          </div>
                        )}
                        {buyer.searchCriteria.priceMax && (
                          <div className="bg-gray-700 rounded px-3 py-2">
                            <span className="text-gray-400">עד: </span>
                            <span className="text-white font-medium">
                              ₪{buyer.searchCriteria.priceMax.toLocaleString()}
                            </span>
                          </div>
                        )}
                        {buyer.searchCriteria.rooms && buyer.searchCriteria.rooms.length > 0 && (
                          <div className="bg-gray-700 rounded px-3 py-2">
                            <span className="text-gray-400">חדרים: </span>
                            <span className="text-white font-medium">
                              {buyer.searchCriteria.rooms.join(', ')}
                            </span>
                          </div>
                        )}
                        {buyer.searchCriteria.cities && buyer.searchCriteria.cities.length > 0 && (
                          <div className="bg-gray-700 rounded px-3 py-2 col-span-2">
                            <span className="text-gray-400">ערים: </span>
                            <span className="text-white font-medium">
                              {buyer.searchCriteria.cities.join(', ')}
                            </span>
                          </div>
                        )}
                        {buyer.searchCriteria.propertyTypes && buyer.searchCriteria.propertyTypes.length > 0 && (
                          <div className="bg-gray-700 rounded px-3 py-2 col-span-2">
                            <span className="text-gray-400">סוג נכס: </span>
                            <span className="text-white font-medium">
                              {buyer.searchCriteria.propertyTypes.map(type => {
                                const typeObj = propertyTypes.find(t => t.value === type);
                                return typeObj ? typeObj.label : type;
                              }).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {buyer.notes && (
                      <div className="mt-4 p-3 bg-gray-700 rounded">
                        <p className="text-sm text-gray-300">{buyer.notes}</p>
                      </div>
                    )}

                    <div className="mt-4 text-xs text-gray-500">
                      נוצר: {new Date(buyer.createdAt).toLocaleDateString('he-IL')}
                    </div>
                  </div>
                ))}
                {showFiltered && filteredBuyers.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <h3 className="text-lg font-bold text-gray-400 mb-4">קונים שסוננו ({filteredBuyers.length})</h3>
                    <div className="space-y-3">
                      {filteredBuyers.map(({ buyer, gate }) => (
                        <div key={buyer.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4 opacity-60">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-lg font-bold text-gray-400">{buyer.name}</h4>
                            <span className="text-xs text-red-400 bg-red-900/30 px-2 py-1 rounded">{gate.reason}</span>
                          </div>
                          <p className="text-sm text-gray-500">{buyer.phone}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          ) : rankedProperties.filter(r => showWeakMatches ? r.score.score >= WEAK_TOGGLE_MIN_SCORE : r.score.score >= DEFAULT_MIN_SCORE).length === 0 ? (
            <div className="text-center py-12">
              <Home size={64} className="mx-auto text-gray-600 mb-4" />
              {matchScope === 'office' ? (
                <>
                  <p className="text-xl text-gray-400 mb-2">אין כרגע התאמות איכותיות בכלל נכסי המשרד</p>
                  <p className="text-gray-500">נסה לשנות את קריטריוני החיפוש של הלקוח</p>
                </>
              ) : hasOffice ? (
                <>
                  <p className="text-xl text-gray-400 mb-2">אין התאמות איכותיות בנכסים שלך</p>
                  <button
                    onClick={() => setMatchScope('office')}
                    className="mt-3 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                  >
                    בדוק את נכסי המשרד
                  </button>
                </>
              ) : (
                <>
                  <p className="text-xl text-gray-400 mb-2">לא נמצאו נכסים מתאימים</p>
                  <p className="text-gray-500">נסה לשנות את קריטריוני החיפוש של הלקוח</p>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {rankedProperties
                .filter(r => showWeakMatches ? r.score.score >= WEAK_TOGGLE_MIN_SCORE : r.score.score >= DEFAULT_MIN_SCORE)
                .map(({ item: property, score: matchScore, rankPosition, totalQualityMatches }) => {
                const match = getMatchForProperty(property.formId);
                const isNotesOpen = showNotes === property.formId;

                return (
                  <div
                    key={property.formId}
                    className="bg-gray-750 border border-gray-700 rounded-lg overflow-hidden hover:border-blue-500 transition-all"
                  >
                    <div className="p-6">
                      <MatchScoreDisplay result={matchScore} rankPosition={rankPosition} totalQualityMatches={totalQualityMatches} />
                      {matchScope === 'office' && (
                        property.isOwnProperty ? (
                          <div className="mb-3 inline-flex items-center gap-1.5 text-xs text-blue-300 bg-blue-900/30 px-2.5 py-1 rounded-lg">
                            <User size={12} />
                            הנכס שלי
                          </div>
                        ) : (
                          <div className="mb-3 inline-flex items-center gap-1.5 text-xs text-amber-300 bg-amber-900/30 px-2.5 py-1 rounded-lg">
                            <Building2 size={12} />
                            {property.ownerDisplayName
                              ? `נכס של ${property.ownerDisplayName}`
                              : 'שייך לסוכן אחר'
                            }
                          </div>
                        )
                      )}
                      {(() => {
                        const propCity = normalizeCityName(property.address?.city);
                        const propNeigh = normalizeNeighborhoodName(property.address?.neighborhood, propCity);
                        const propData: PropertyData = {
                          price: property.property.price,
                          rooms: property.property.rooms,
                          size: property.property.size,
                          propertyType: property.property.propertyType,
                          city: propCity,
                          neighborhood: propNeigh,
                          hasParking: (property.property.parkingSpots ?? 0) > 0,
                          hasElevator: property.property.hasElevator,
                          hasSaferoom: property.property.hasSaferoom,
                          hasBalcony: property.property.hasBalcony,
                          propertyCondition: property.property.propertyCondition,
                        };
                        const crit: BuyerCriteria = {
                          priceMin: client.searchCriteria.priceMin,
                          priceMax: client.searchCriteria.priceMax,
                          rooms: client.searchCriteria.rooms,
                          cities: client.searchCriteria.cities,
                          neighborhoods: client.searchCriteria.neighborhoods,
                          propertyTypes: client.searchCriteria.propertyTypes,
                          minSize: client.searchCriteria.minSize,
                          maxSize: client.searchCriteria.maxSize,
                        };
                        const conf = calculateConfidence(propData, crit);
                        const recState = getRecState(client.id, property.formId);
                        const topN = rankedProperties
                          .filter(r => r.score.score >= AI_MATCH_MIN_SCORE)
                          .slice(0, AI_MATCH_RECOMMENDATION_LIMIT)
                          .some(r => r.item.formId === property.formId);
                        if (!topN) return null;
                        return (
                          <AIMatchRecommendation
                            buyerId={client.id}
                            propertyId={property.formId}
                            loading={recState.loading}
                            error={recState.error}
                            recommendation={recState.recommendation}
                            confidence={conf}
                            matchScore={matchScore.score}
                            rankPosition={rankPosition}
                            totalQualityMatches={totalQualityMatches}
                            onGenerate={() => generateRec(client.id, property.formId, crit, propData, matchScore, { rankPosition, totalQualityMatches, matchScope })}
                          />
                        );
                      })()}
                      {match && (() => {
                        const currentStatus = statusOptions.find(s => s.value === match.status);
                        return currentStatus ? (
                          <div className={`${currentStatus.color} rounded-t-lg -mx-6 -mt-6 mb-4 px-6 py-3`}>
                            <div className="flex items-center gap-2 text-white font-bold text-lg">
                              <currentStatus.icon size={20} />
                              <span>סטטוס נוכחי: {currentStatus.label}</span>
                            </div>
                          </div>
                        ) : null;
                      })()}

                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-white">
                              {property.property.propertyTypeLabel}
                            </h3>
                          </div>
                          <div className="flex items-center gap-2 text-gray-300">
                            <MapPin size={16} />
                            <span>
                              {property.address.street} {property.address.houseNumber}, {property.address.city}
                            </span>
                          </div>
                        </div>

                        {property.imageUrls?.propertyImages?.[0] && (
                          <img
                            src={property.imageUrls.propertyImages[0]}
                            alt="נכס"
                            className="w-32 h-32 object-cover rounded-lg"
                          />
                        )}
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                            <DollarSign size={16} />
                            <span>מחיר</span>
                          </div>
                          <p className="text-white font-bold">
                            ₪{property.property.price.toLocaleString()}
                          </p>
                        </div>

                        <div className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                            <Home size={16} />
                            <span>חדרים</span>
                          </div>
                          <p className="text-white font-bold">{property.property.rooms}</p>
                        </div>

                        <div className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                            <Maximize2 size={16} />
                            <span>גודל</span>
                          </div>
                          <p className="text-white font-bold">{property.property.size} מ"ר</p>
                        </div>

                        <div className="bg-gray-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-gray-400 text-sm mb-1">
                            <Calendar size={16} />
                            <span>בשוק</span>
                          </div>
                          <p className="text-white font-bold">{property.property.daysOnMarket} ימים</p>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                          קומה {property.property.floor}
                        </span>
                        <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                          {property.property.bathrooms} חדרי רחצה
                        </span>
                        <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                          {property.property.parkingSpots} חניות
                        </span>
                        {property.property.hasBalcony && (
                          <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                            מרפסת {property.property.balconySize}מ"ר
                          </span>
                        )}
                        {property.property.hasElevator && (
                          <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                            מעלית
                          </span>
                        )}
                        {property.property.hasSaferoom && (
                          <span className="bg-gray-700 text-gray-300 text-xs px-3 py-1 rounded-full">
                            חדר מוגן
                          </span>
                        )}
                      </div>

                      <div className="border-t border-gray-700 pt-4 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">
                            סטטוס
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {statusOptions.map(status => (
                              <button
                                key={status.value}
                                onClick={() => handleUpdateStatus(property.formId, status.value as PropertyClientMatch['status'])}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                                  match?.status === status.value
                                    ? `${status.color} text-white`
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                }`}
                              >
                                <status.icon size={16} />
                                <span className="text-sm">{status.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          {match?.notes && !isNotesOpen && (
                            <div className="bg-gray-700 rounded-lg p-3 mb-2">
                              <p className="text-sm text-gray-300">{match.notes}</p>
                            </div>
                          )}

                          {isNotesOpen ? (
                            <div className="space-y-2">
                              <textarea
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                                placeholder="הוסף הערה..."
                                className="w-full px-4 py-3 rounded-lg bg-gray-700 border border-gray-600 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 h-24"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleSaveNote(property.formId)}
                                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                                >
                                  שמור הערה
                                </button>
                                <button
                                  onClick={() => {
                                    setShowNotes(null);
                                    setNoteText('');
                                  }}
                                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                                >
                                  ביטול
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setShowNotes(property.formId);
                                setNoteText(match?.notes || '');
                              }}
                              className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                            >
                              <MessageSquare size={16} />
                              {match?.notes ? 'ערוך הערה' : 'הוסף הערה'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
                {showFiltered && filteredProperties.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-700">
                    <h3 className="text-lg font-bold text-gray-400 mb-4">נכסים שסוננו ({filteredProperties.length})</h3>
                    <div className="space-y-3">
                      {filteredProperties.map(({ property: prop, gate }) => (
                        <div key={prop.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4 opacity-60">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-lg font-bold text-gray-400">
                              {prop.property?.propertyType || 'נכס'} · {prop.address?.city || 'לא צוין'}
                            </h4>
                            <span className="text-xs text-red-400 bg-red-900/30 px-2 py-1 rounded">{gate.reason}</span>
                          </div>
                          <div className="text-sm text-gray-500 flex gap-4">
                            {prop.property?.rooms && <span>{prop.property.rooms} חדרים</span>}
                            {prop.property?.size && <span>{prop.property.size} מ"ר</span>}
                            {prop.property?.price && <span>₪{prop.property.price.toLocaleString()}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-700 bg-gray-750 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
          >
            סגור
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientPropertiesModal;
